<template>
  <div>
    <div class="toolbar">
      <span></span>
      <div class="toolbar-right">
        <div class="seg" role="tablist" aria-label="มุมมอง">
          <span class="seg-lbl">มุมมอง:</span>
          <button
            type="button"
            class="seg-btn"
            :class="{ active: viewMode === 'long' }"
            @click="viewMode = 'long'"
          >
            🗓️ ตามวันที่
          </button>
          <button
            type="button"
            class="seg-btn"
            :class="{ active: viewMode === 'pivot' }"
            @click="viewMode = 'pivot'"
          >
            💳 ตามวิธีชำระ
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <b-table
      id="PersonalDfTable"
      ref="PersonalDfTable"
      class="report-table"
      small
      sort-icon-left
      head-variant="light"
      :busy="tableBusy"
      :items="activeRows"
      :fields="activeFields"
      :responsive="true"
      show-empty
    >
      <template #cell(date)="row">{{ row.value }}</template>
      <template #cell(paid)="row">{{ formatNumber(row.value) }}</template>
      <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
      <template #cell(feePercent)="row">{{ row.value }}%</template>
      <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
      <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>

      <template #custom-foot>
        <b-tr v-if="viewMode === 'long'" variant="secondary" class="text-bold text-right">
          <td class="text-left">รวม</td>
          <td></td>
          <td>{{ formatNumber(totalSummary.paid) }}</td>
          <td>{{ formatNumber(totalSummary.df) }}</td>
          <td></td>
          <td>{{ formatNumber(totalSummary.feeAmount) }}</td>
          <td>{{ formatNumber(totalSummary.netDf) }}</td>
        </b-tr>
        <b-tr v-else variant="secondary" class="text-bold text-right">
          <td class="text-left">รวม</td>
          <td>{{ formatNumber(totalSummary.paid) }}</td>
          <td v-for="m in distinctPaymentMethods" :key="m.keyName">
            {{ formatNumber(methodTotal(m.keyName)) }}
          </td>
          <td>{{ formatNumber(totalSummary.df) }}</td>
          <td class="text-danger">({{ formatNumber(totalSummary.feeAmount) }})</td>
          <td>{{ formatNumber(totalSummary.netDf) }}</td>
        </b-tr>
      </template>
      <template #empty="">
        <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
      </template>
    </b-table>

    <!-- Summary by Payment Type -->
    <b-card v-if="summaryByPayment.length > 0" class="mt-4" no-body>
      <b-card-header>
        <strong>สรุปตามวิธีชำระ</strong>
      </b-card-header>
      <b-card-body>
        <b-table
          id="PersonalDfSummaryByPayment"
          class="report-table"
          :items="summaryByPayment"
          :fields="paymentSummaryFields"
          small
          head-variant="light"
          show-empty
        >
          <template #cell(paid)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(df)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(feePercent)="row">{{ row.value }}%</template>
          <template #cell(feeAmount)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(netDf)="row">{{ formatNumber(row.value) }}</template>

          <template #custom-foot>
            <b-tr class="text-right font-weight-bold">
              <td class="text-left">รวมยอดชำระ</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>{{ formatNumber(totalSummary.paid) }}</td>
            </b-tr>
            <b-tr class="text-right font-weight-bold">
              <td class="text-left">รวม DF</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>{{ formatNumber(totalSummary.df) }}</td>
            </b-tr>
            <b-tr class="text-right font-weight-bold">
              <td class="text-left">รวมค่าธรรมเนียม</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="text-danger">({{ formatNumber(totalSummary.feeAmount) }})</td>
            </b-tr>
            <b-tr v-if="totalLabDf > 0" class="text-right font-weight-bold">
              <td class="text-left">รวมค่าแลป</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="text-danger">({{ formatNumber(totalLabDf) }})</td>
            </b-tr>
            <b-tr v-if="isDeducted && deductAmount > 0" class="text-right font-weight-bold">
              <td class="text-left">หักค่าใช้จ่าย 10% ตามสัญญาแพทย์</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="text-danger">({{ formatNumber(deductAmount) }})</td>
            </b-tr>
            <b-tr variant="secondary" class="text-right font-weight-bold">
              <td class="text-left">รายได้สุทธิ</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>{{ formatNumber(finalNetDf) }}</td>
            </b-tr>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Loading from "@/components/Loading";
import moment from "moment";

export default {
  name: "PersonalDfByPaymentTypeReport",
  components: {
    Loading,
  },
  props: {
    doctorUid: {
      type: [String, Number],
      default: null,
    },
    dateRange: {
      type: Array,
      default: () => [],
    },
    labReport: {
      type: Array,
      default: () => [],
    },
    incomeList: {
      type: Array,
      default: () => [],
    },
    isDeducted: {
      type: Boolean,
      default: false,
    },
    deductAmount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isLoading: false,
      tableBusy: false,

      groupBy: "day", // default groupBy
      viewMode: "long", // 'long' | 'pivot'

      reportData: [],
      rawTableHeader: [],

      longFields: [
        { key: "date", label: "วัน/เดือน/ปี" },
        { key: "paymentName", label: "วิธีชำระ" },
        { key: "paid", label: "ยอดชำระ", class: "text-right" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feePercent", label: "ค่าธรรมเนียม %", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],

      paymentSummaryFields: [
        { key: "name", label: "วิธีชำระ" },
        { key: "paid", label: "ยอดชำระ", class: "text-right" },
        { key: "df", label: "DF", class: "text-right" },
        { key: "feePercent", label: "ค่าธรรมเนียม %", class: "text-right" },
        { key: "feeAmount", label: "ค่าธรรมเนียม", class: "text-right" },
        { key: "netDf", label: "DF สุทธิ", class: "text-right font-weight-bold" },
      ],
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      paymentMethodList: "moduleUser/getPaymentMethodList",
    }),

    finalNetDf() {
      return this.totalSummary.netDf - this.totalLabDf - this.deductAmount;
    },

    distinctPaymentMethods() {
      const map = new Map();
      this.formattedRows.forEach(r => {
        if (!map.has(r.keyName)) {
          map.set(r.keyName, {
            keyName: r.keyName,
            name: r.paymentName,
            feePercent: r.feePercent,
          });
        }
      });
      return [...map.values()].sort((a, b) =>
        String(a.keyName).localeCompare(String(b.keyName))
      );
    },

    pivotedRows() {
      const byDate = new Map();
      this.formattedRows.forEach(r => {
        if (!byDate.has(r.date)) {
          byDate.set(r.date, {
            date: r.date,
            _paid: 0,
            _df: 0,
            _fee: 0,
            _netDf: 0,
          });
        }
        const row = byDate.get(r.date);
        row[`method_${r.keyName}`] = (row[`method_${r.keyName}`] || 0) + r.df;
        row._paid += r.paid;
        row._df += r.df;
        row._fee += r.feeAmount;
        row._netDf += r.netDf;
      });
      return [...byDate.values()];
    },

    pivotFields() {
      const methodCols = this.distinctPaymentMethods.map(m => ({
        key: `method_${m.keyName}`,
        label: `DF ${m.name}`,
        class: "text-right",
        formatter: v => this.formatNumber(v || 0),
      }));
      return [
        { key: "date", label: "วัน/เดือน/ปี" },
        {
          key: "_paid",
          label: "ยอดชำระรวม (ก่อนหัก DF)",
          class: "text-right",
          formatter: v => this.formatNumber(v || 0),
        },
        ...methodCols,
        {
          key: "_df",
          label: "รวม DF",
          class: "text-right",
          formatter: v => this.formatNumber(v || 0),
        },
        {
          key: "_fee",
          label: "รวมค่าธรรมเนียม",
          class: "text-right text-danger",
          formatter: v => this.formatNumber(v || 0),
        },
        {
          key: "_netDf",
          label: "DF สุทธิ",
          class: "text-right font-weight-bold",
          formatter: v => this.formatNumber(v || 0),
        },
      ];
    },

    paidByDateAndMethod() {
      const map = {};
      (this.incomeList || []).forEach(item => {
        // Normalize to YYYY-MM-DD so displayDate produces the same key as
        // the DF report data (which already comes as a date-only string).
        const normalized = moment(item.creationDt).format("YYYY-MM-DD");
        const dateKey = this.displayDate(normalized);
        (item.paymentAllocations || []).forEach(pa => {
          const k = `${dateKey}|${pa.id}`;
          map[k] = (map[k] || 0) + parseFloat(pa.amount || 0);
        });
      });
      return map;
    },

    activeFields() {
      return this.viewMode === "pivot" ? this.pivotFields : this.longFields;
    },

    activeRows() {
      return this.viewMode === "pivot" ? this.pivotedRows : this.formattedRows;
    },

    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },

    canFetch() {
      return this.doctorUid && this.dateRange.length === 2;
    },

    // Format data into rows
    formattedRows() {
      const rows = [];
      const paidMap = this.paidByDateAndMethod;

      this.reportData.forEach(doctor => {
        doctor.dates.forEach(d => {
          d.dfByPaymentType.forEach(pm => {
            if (pm.value > 0) {
              const feePercent = this.getProcessingFee(pm.keyName);
              const feeAmount = (pm.value * feePercent) / 100;
              const dateKey = this.displayDate(d.creationDt);

              rows.push({
                date: dateKey,
                paymentName: pm.name,
                keyName: pm.keyName,
                paid: paidMap[`${dateKey}|${pm.keyName}`] || 0,
                df: pm.value,
                feePercent: feePercent,
                feeAmount: feeAmount,
                netDf: pm.value - feeAmount,
              });
            }
          });
        });
      });

      return rows;
    },

    // Summary by payment type
    summaryByPayment() {
      const paymentTotals = {};

      this.formattedRows.forEach(row => {
        if (!paymentTotals[row.keyName]) {
          paymentTotals[row.keyName] = {
            name: row.paymentName,
            keyName: row.keyName,
            paid: 0,
            df: 0,
            feePercent: row.feePercent,
            feeAmount: 0,
            netDf: 0,
          };
        }
        paymentTotals[row.keyName].paid += row.paid;
        paymentTotals[row.keyName].df += row.df;
        paymentTotals[row.keyName].feeAmount += row.feeAmount;
        paymentTotals[row.keyName].netDf += row.netDf;
      });

      return Object.values(paymentTotals);
    },

    totalLabDf() {
      return this.labReport.reduce((sum, item) => sum + (item.dfAmount || 0), 0);
    },

    // Total summary
    totalSummary() {
      let totalPaid = 0;
      let totalDf = 0;
      let totalFee = 0;

      this.formattedRows.forEach(row => {
        totalPaid += row.paid;
        totalDf += row.df;
        totalFee += row.feeAmount;
      });

      return {
        paid: totalPaid,
        df: totalDf,
        feeAmount: totalFee,
        labDf: this.totalLabDf,
        netDf: totalDf - totalFee,
      };
    },
  },
  watch: {
    doctorUid() {
      this.reportData = [];
    },
    dateRange() {
      this.reportData = [];
    },
  },
  methods: {
    ...mapActions({
      fetchDfByPaymentType: "moduleReport/fetchDfByPaymentType",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
    }),

    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },

    formatNumber(num) {
      if (num === null || num === undefined) return "-";
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    methodTotal(keyName) {
      return this.formattedRows.reduce(
        (acc, r) => acc + (r.keyName === keyName ? r.df : 0),
        0
      );
    },

    displayDate(date) {
      if (this.groupBy === "year")
        return "ปี " + moment(date, "YYYY").locale("th").format("YYYY");
      if (this.groupBy === "month")
        return moment(date, "YYYY-MM").locale("th").format("MMM YY");
      return moment(date, "YYYY-MM-DD").locale("th").format("D MMM YY");
    },

    getProcessingFee(keyName) {
      const method = this.paymentMethodList.find(m => m.id === keyName);
      return method?.processingFee || 0;
    },

    async fetchReport() {
      if (!this.canFetch) return;

      this.isLoading = true;
      this.tableBusy = true;

      try {
        const res = await this.fetchDfByPaymentType({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          groupBy: this.groupBy,
          actionUid: this.doctorUid,
        });

        this.reportData = res.data.item || [];
        this.rawTableHeader = res.data.header?.receiptPayment || [];
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
        this.tableBusy = false;
      }
    },

    // Methods exposed for parent component
    getSummary() {
      return this.totalSummary;
    },
    getSummaryByPayment() {
      return this.summaryByPayment;
    },
  },
};
</script>

<style>
</style>
